import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger';

// Fetch amenities with pagination & filters
export const fetchAmenities = createAsyncThunk(
  'amenity/fetchAmenities',
  async ({ page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Fetching amenities with page:', page, 'and limit:', limit);
      const response = await api.amenities.getAll({ page, limit, filters });
      if (response.data.status) {
        log.info('Amenities fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch amenities:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching amenities:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching amenities');
    }
  }
);

// Search amenities with keyword
export const searchAmenities = createAsyncThunk(
  'amenity/searchAmenities',
  async ({ keyword, page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Searching amenities with keyword:', keyword);
      const response = await api.amenities.search({ keyword, page, limit, ...filters });
      if (response.data.status) {
        log.info('Amenities search completed successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to search amenities:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error searching amenities:', error);
      return rejectWithValue(error.response?.data?.message || 'Error searching amenities');
    }
  }
);

// Create amenity
export const createAmenity = createAsyncThunk(
  'amenity/createAmenity',
  async (formData, { rejectWithValue }) => {
    try {
      log.info('Creating amenity with data:', formData);
      const response = await api.amenities.create(formData);
      if (response.data.status) {
        log.info('Amenity created successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to create amenity:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error creating amenity:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create amenity');
    }
  }
);

// Update amenity
export const updateAmenity = createAsyncThunk(
  'amenity/updateAmenity',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      log.info('Updating amenity with data:', formData);
      const response = await api.amenities.update(id, formData);
      if (response.data.status) {
        log.info('Amenity updated successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to update amenity:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating amenity:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to update amenity');
    }
  }
);

// Delete amenity
export const deleteAmenity = createAsyncThunk(
  'amenity/deleteAmenity',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting amenity with ID:', id);
      const response = await api.amenities.delete(id);
      if (response.data.status) {
        log.info('Amenity deleted successfully:', id);
        return id;
      } else {
        log.warn('Failed to delete amenity:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting amenity:', error);
      return rejectWithValue(error.response?.data?.message || 'Error deleting amenity');
    }
  }
);

// Bulk delete amenities
export const bulkDeleteAmenities = createAsyncThunk(
  'amenity/bulkDeleteAmenities',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.amenities.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting amenities');
    }
  }
);

// Bulk activate/deactivate amenities
export const bulkUpdateAmenityStatus = createAsyncThunk(
  'amenity/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.amenities.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating amenity status');
    }
  }
);

// Fetch a single amenity by ID
export const fetchAmenityById = createAsyncThunk(
  'amenity/fetchAmenityById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching amenity with ID:', id);
      const response = await api.amenities.getById(id);
      if (response.data.status) {
        log.info('Amenity fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch amenity:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching amenity:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching amenity');
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  page: 1,
  pages: 0,
  limit: 10,
  loading: false,
  error: null
};

const amenitySlice = createSlice({
  name: 'amenity',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch amenities
      .addCase(fetchAmenities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAmenities.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.amenities || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(fetchAmenities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search amenities
      .addCase(searchAmenities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchAmenities.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.amenities || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(searchAmenities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create amenity
      .addCase(createAmenity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAmenity.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createAmenity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update amenity
      .addCase(updateAmenity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAmenity.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateAmenity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete amenity
      .addCase(deleteAmenity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAmenity.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deleteAmenity.rejected, (state) => {
        state.loading = false;
      })
      // Bulk delete amenities
      .addCase(bulkDeleteAmenities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteAmenities.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteAmenities.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateAmenityStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateAmenityStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateAmenityStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default amenitySlice.reducer; 